
import React, { FC, useState, useEffect, useMemo } from 'react';
import { AttendanceRecord, AttendanceStatus, CalendarEvent, ClassRoom, PreReportedAbsence, StudentConnection, UserProfile, AcademicYear } from '../types';
import { Avatar, Header } from './common';
import { translateContent } from '../lib/gemini';
import { 
    getInstructionalDatesToDate, 
    computeStudentAttendanceMetrics, 
    computeMonthlyAttendanceLedger,
    MonthLedgerRow,
    StudentAttendanceMetrics
} from '../lib/attendanceUtils';

// FIX: Use React.FC to correctly type the component and allow the 'key' prop.
const AttachmentDisplay: FC<{ name: string; url: string; mimeType: string }> = ({ name, url, mimeType }) => {
  let icon = 'attachment';
  if (mimeType?.startsWith('image/') || url?.includes('.jpg') || url?.includes('.png')) icon = 'image';
  if (mimeType === 'application/pdf' || url?.includes('.pdf')) icon = 'picture_as_pdf';
  if (mimeType?.includes('document') || mimeType?.includes('msword')) icon = 'article';

  return (
    <div className="mt-2 p-2 bg-gray-100 rounded-lg flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 overflow-hidden flex-1">
        <span className="material-icons-round text-gray-600 !text-base">{icon}</span>
        <span className="text-xs text-gray-700 font-medium truncate">{name}</span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <a href={url} target="_blank" rel="noopener noreferrer" title="View" className="p-1 text-gray-500 hover:text-indigo-600 rounded-full">
          <span className="material-icons-round !text-base">visibility</span>
        </a>
        <a href={url} download={name} title="Download" className="p-1 text-gray-500 hover:text-indigo-600 rounded-full">
          <span className="material-icons-round !text-base">download</span>
        </a>
      </div>
    </div>
  );
};

const EventItem: React.FC<{
    event: CalendarEvent, 
    eventTypeColors: any, 
    canManage: boolean, 
    onEditEvent?: (event: CalendarEvent) => void, 
    onDeleteEvent?: (id: string) => void, 
    setViewingImageUrl?: (url: string) => void, 
    t: (key: string) => string,
    userLanguage?: string
}> = ({ event, eventTypeColors, canManage, onEditEvent, onDeleteEvent, setViewingImageUrl, t, userLanguage = 'en' }) => {
    const [translatedDesc, setTranslatedDesc] = useState<string | null>(null);
    const [translatedTitle, setTranslatedTitle] = useState<string | null>(null);
    const [isTranslated, setIsTranslated] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    const targetLang = userLanguage || 'en';

    useEffect(() => {
        setTranslatedDesc(null);
        setTranslatedTitle(null);
        setIsTranslated(false);
    }, [targetLang, event.id, event.title, event.description]);

    const handleTranslate = async () => {
        if (!translatedDesc && !translatedTitle) {
            setIsTranslating(true);
            try {
                const tDesc = event.description ? await translateContent(event.description, targetLang) : '';
                const tTitle = await translateContent(event.title, targetLang);
                setTranslatedDesc(tDesc);
                setTranslatedTitle(tTitle);
                setIsTranslated(true);
            } catch (err) {
                console.error("Event translation error:", err);
            } finally {
                setIsTranslating(false);
            }
        } else {
            setIsTranslated(!isTranslated);
        }
    };

    return (
        <div className="flex items-start gap-2">
             <div className={`w-2 h-2 mt-1.5 rounded-full ${eventTypeColors[event.type]}`}></div>
             <div className="flex-1">
                <p className="text-sm font-bold">{isTranslated ? translatedTitle : event.title}</p>
                <p className="text-xs text-gray-500">{t(event.type)}</p>
                {event.description && <p className="text-xs text-gray-500 whitespace-pre-wrap">{isTranslated ? translatedDesc : event.description}</p>}
                
                <button onClick={handleTranslate} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 mt-1">
                    {isTranslating ? t('processing') : (isTranslated ? t('show_original') : t('translate'))}
                </button>

                {event.attachments && event.attachments.map((attachment, index) => {
                    if (attachment.mimeType.startsWith('image/') && setViewingImageUrl) {
                        return (
                            <img
                                key={index}
                                src={attachment.url}
                                alt={attachment.name}
                                className="mt-2 rounded-lg cursor-pointer w-full h-auto max-h-48 object-cover"
                                onClick={() => setViewingImageUrl(attachment.url)}
                            />
                        );
                    }
                    return (
                        <AttachmentDisplay
                            key={index}
                            name={attachment.name}
                            url={attachment.url}
                            mimeType={attachment.mimeType}
                        />
                    );
                })}
             </div>
              {canManage && onEditEvent && onDeleteEvent && (
                <div className="flex gap-1">
                    <button onClick={() => onEditEvent(event)} className="p-1 text-gray-400 hover:text-indigo-600"><span className="material-icons-round text-sm">edit</span></button>
                    <button onClick={() => onDeleteEvent(event.id)} className="p-1 text-gray-400 hover:text-red-600"><span className="material-icons-round text-sm">delete</span></button>
                </div>
              )}
         </div>
    );
}

export const SmartCalendar = ({ user, records, events, preReportedAbsences, onEditEvent, onDeleteEvent, onEditAbsence, setViewingImageUrl, t, userLanguage }: {
    user: UserProfile;
    records: AttendanceRecord[];
    events: CalendarEvent[];
    preReportedAbsences?: PreReportedAbsence[];
    onEditEvent?: (event: CalendarEvent) => void;
    onDeleteEvent?: (eventId: string) => void;
    onEditAbsence?: (absence: PreReportedAbsence) => void;
    setViewingImageUrl?: (url: string | null) => void;
    t: (key: string) => string;
    userLanguage?: string;
}) => {
    const [viewingDate, setViewingDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const recordsByDate = records.reduce((acc, record) => {
        acc[record.date] = record.status;
        return acc;
    }, {} as Record<string, AttendanceStatus>);
    
    const preReportedByDate = (preReportedAbsences || []).reduce((acc, p) => {
        acc[p.date] = p;
        return acc;
    }, {} as Record<string, PreReportedAbsence>);
    
    const eventsByDate = events.reduce((acc, event) => {
        // This logic handles date ranges
        const start = new Date(event.startDate + 'T00:00:00');
        const end = new Date(event.endDate + 'T00:00:00');
        for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = d.toISOString().split('T')[0];
            if (!acc[dateStr]) acc[dateStr] = [];
            acc[dateStr].push(event);
        }
        return acc;
    }, {} as Record<string, CalendarEvent[]>);

    const statusStyles: Record<AttendanceStatus, string> = {
        present: 'bg-green-100 text-green-700',
        absent: 'bg-red-100 text-red-700',
        late: 'bg-yellow-100 text-yellow-700',
    };
    
    const eventTypeColors: Record<CalendarEvent['type'], string> = {
        holiday: 'bg-blue-500',
        school_event: 'bg-indigo-500',
        meeting: 'bg-gray-500',
        exam: 'bg-purple-500',
        deadline: 'bg-red-500',
        class_event: 'bg-green-500',
        urgent_off: 'bg-red-600',
    };

    const year = viewingDate.getFullYear();
    const month = viewingDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const selectedDateEvents = eventsByDate[selectedDate] || [];
    const selectedDateAttendance = recordsByDate[selectedDate];
    const selectedDatePreReported = preReportedByDate[selectedDate];

    const renderCalendar = () => {
        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="p-2"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const status = recordsByDate[dateStr];
            const dayEvents = eventsByDate[dateStr] || [];
            const isPreReported = !!preReportedByDate[dateStr];
            
            const isSelected = dateStr === selectedDate;

            days.push(
                <button key={day} onClick={() => setSelectedDate(dateStr)} className="flex flex-col items-center justify-start p-1 h-16 rounded-lg transition-colors hover:bg-gray-100">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium ${isSelected ? 'bg-indigo-600 text-white' : status ? statusStyles[status] : 'text-gray-700'}`}>
                        {day}
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap justify-center">
                        {isPreReported && (
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Reported Absence"></div>
                        )}
                        {dayEvents.slice(0, 3).map(event => (
                           <div key={event.id} className={`w-1.5 h-1.5 rounded-full ${eventTypeColors[event.type]}`}></div>
                        ))}
                    </div>
                </button>
            );
        }
        return days;
    };

    const changeMonth = (offset: number) => {
        setViewingDate(currentDate => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() + offset);
            return newDate;
        });
    };

    return (
        <div className="bg-white p-4 rounded-xl shadow-sm border">
            <h3 className="font-bold text-lg mb-4">{t('smart_calendar')}</h3>
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => changeMonth(-1)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round">chevron_left</span>
                </button>
                <h4 className="font-bold">{viewingDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h4>
                <button onClick={() => changeMonth(1)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round">chevron_right</span>
                </button>
            </div>
            <div className="grid grid-cols-7 gap-2 text-center text-xs font-bold text-gray-400 mb-2">
                <div>S</div><div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div>
            </div>
            <div className="grid grid-cols-7 gap-1">
                {renderCalendar()}
            </div>
             <div className="mt-4 pt-4 border-t">
                 <h4 className="font-bold mb-2">{new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
                 <div className="space-y-2">
                    {selectedDatePreReported && (
                        <div className="flex flex-col bg-orange-50 p-3 rounded-lg border border-orange-100">
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                    <div>
                                        <p className="text-sm font-bold text-orange-800">{t('report_absence')}</p>
                                        <p className="text-xs text-orange-700">{selectedDatePreReported.reason}</p>
                                    </div>
                                </div>
                                {onEditAbsence && (
                                    <button 
                                        onClick={() => onEditAbsence(selectedDatePreReported)}
                                        className="text-orange-600 bg-white p-1 rounded hover:bg-orange-100 border border-orange-200 text-xs font-bold px-2"
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                            {selectedDatePreReported.attachmentUrl && (
                                <AttachmentDisplay name="Attachment" url={selectedDatePreReported.attachmentUrl} mimeType="image/png" />
                            )}
                        </div>
                    )}
                    {selectedDateAttendance && (
                         <div className="flex items-center gap-2">
                             <div className={`w-2 h-2 rounded-full ${ {present: 'bg-green-500', absent: 'bg-red-500', late: 'bg-yellow-500'}[selectedDateAttendance]}`}></div>
                             <p className="text-sm">Attendance: <span className="font-bold">{t(selectedDateAttendance)}</span></p>
                         </div>
                    )}
                    {selectedDateEvents.map(event => {
                        const canManage = user.role === 'principal' || (user.role === 'teacher' && event.creatorId === user.id);
                        return (
                            <EventItem 
                                key={event.id}
                                event={event}
                                eventTypeColors={eventTypeColors}
                                canManage={canManage}
                                onEditEvent={onEditEvent}
                                onDeleteEvent={onDeleteEvent}
                                setViewingImageUrl={setViewingImageUrl}
                                t={t}
                                userLanguage={userLanguage || user.language}
                            />
                        )
                    })}
                    {!selectedDateAttendance && selectedDateEvents.length === 0 && !selectedDatePreReported && (
                        <p className="text-sm text-gray-400">No events or attendance recorded.</p>
                    )}
                 </div>
             </div>
        </div>
    );
};

export const AttendanceTaker = ({ 
    user, 
    cls, 
    students, 
    preReportedAbsences, 
    todaysAttendance, 
    allAttendanceRecords,
    onSave, 
    onBack, 
    onLogout, 
    onEditProfile, 
    t, 
    onClose 
}: {
    user?: UserProfile;
    cls?: ClassRoom;
    students?: StudentConnection[];
    preReportedAbsences?: PreReportedAbsence[];
    todaysAttendance?: AttendanceRecord[];
    allAttendanceRecords?: AttendanceRecord[];
    onSave?: (records: Omit<AttendanceRecord, 'id'>[]) => Promise<void>;
    onBack?: () => void;
    onLogout?: () => void;
    onEditProfile?: () => void;
    t?: (key: string, reps?: any) => string;
    onClose?: () => void;
}) => {
    // If strict props are missing, but we can display, don't block.
    if (!user || !cls || !students || !onSave) {
        return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="bg-white p-6 rounded-lg relative"><button onClick={onClose || onBack} className="absolute top-2 right-2 text-gray-500"><span className="material-icons-round">close</span></button>Attendance Taker Error: Missing critical props ({!user ? 'user' : ''} {!cls ? 'cls' : ''} {!students ? 'students' : ''} {!onSave ? 'onSave' : ''})</div></div>;
    }

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    // Compute 7 days ago minimum allowed date (Past edits allowed strictly up to 1 week in the past)
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const minDateStr = sevenDaysAgo.toISOString().split('T')[0];
    const maxDateStr = todayStr;

    const [selectedDate, setSelectedDate] = useState<string>(todayStr);

    // Initialize attendance for the selected date
    const getRecordsForDate = (dateStr: string) => {
        if (allAttendanceRecords && allAttendanceRecords.length > 0) {
            return allAttendanceRecords.filter(r => r.classId === cls.id && r.date === dateStr);
        }
        if (dateStr === todayStr && todaysAttendance) {
            return todaysAttendance;
        }
        return [];
    };

    const computeInitialAttendance = (dateStr: string) => {
        const recordsForDate = getRecordsForDate(dateStr);
        return students.reduce((acc, student) => {
            const existingRecord = recordsForDate.find(a => a.studentId === student.id);
            const preReported = preReportedAbsences?.find(p => p.studentId === student.id && p.date === dateStr);
            acc[student.id] = existingRecord ? existingRecord.status : (preReported ? 'absent' : 'present');
            return acc;
        }, {} as Record<string, AttendanceStatus>);
    };

    const [attendance, setAttendance] = useState<Record<string, AttendanceStatus>>(() => computeInitialAttendance(todayStr));
    const [notes, setNotes] = useState<Record<string, string>>({}); // for potential notes

    // Update attendance state whenever selectedDate changes
    useEffect(() => {
        setAttendance(computeInitialAttendance(selectedDate));
    }, [selectedDate, cls.id, students, allAttendanceRecords, todaysAttendance, preReportedAbsences]);

    const setStudentStatus = (studentId: string, status: AttendanceStatus) => {
        setAttendance(prev => ({ ...prev, [studentId]: status }));
    };

    const handleSave = async () => {
        const records: Omit<AttendanceRecord, 'id'>[] = students.map(student => {
            const record: Omit<AttendanceRecord, 'id'> = {
                studentId: student.id,
                classId: cls.id,
                date: selectedDate,
                status: attendance[student.id],
                academicYearId: cls.academicYearId,
                recordedById: user.id,
            };

            const studentNote = notes[student.id];
            if (studentNote && studentNote.trim()) {
                record.notes = studentNote;
            }

            return record;
        });
        await onSave(records);
        if (onBack) onBack();
    };

    const translate = t || ((key: string) => key);

    const AttendanceButton = ({ status, isSelected, onClick }: { status: AttendanceStatus, isSelected: boolean, onClick: () => void }) => {
        const styles: Record<AttendanceStatus, string> = {
            present: 'bg-green-100 text-green-700',
            absent: 'bg-red-100 text-red-700',
            late: 'bg-yellow-100 text-yellow-700',
        };
        const selectedStyles = 'ring-2 ring-offset-1 ring-indigo-500 font-black shadow-sm';
        return (
            <button
                type="button"
                onClick={onClick}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${styles[status]} ${isSelected ? selectedStyles : 'opacity-60 hover:opacity-100'}`}
            >
                {translate(status)}
            </button>
        );
    };

    // Calculate quick relative date options (Today, Yesterday, -2d, ... -7d)
    const quickDateOptions = React.useMemo(() => {
        const opts = [];
        for (let i = 0; i <= 7; i++) {
            const d = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
            const dateStr = d.toISOString().split('T')[0];
            const label = i === 0 ? 'Today' : i === 1 ? 'Yesterday' : `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`;
            opts.push({ dateStr, label, daysAgo: i });
        }
        return opts;
    }, []);

    const isBackfill = selectedDate !== todayStr;
    const existingCountForDate = getRecordsForDate(selectedDate).length;

    return (
        <div className="flex flex-col h-full bg-white">
            <Header
                title={translate('take_attendance')}
                subtitle={cls.name}
                user={user}
                onBack={onBack}
                onLogout={onLogout!}
                onEditProfile={onEditProfile!}
            />
            {/* 7-Day Date Selector & Backfill Window Bar */}
            <div className="p-4 bg-indigo-50/80 border-b border-indigo-200 space-y-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Attendance Roll Date:</span>
                            {isBackfill ? (
                                <span className="bg-amber-100 text-amber-900 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-300 flex items-center gap-1">
                                    <span className="material-icons-round !text-xs">history</span>
                                    Backfill / Past Edit
                                </span>
                            ) : (
                                <span className="bg-green-100 text-green-900 text-[11px] font-bold px-2 py-0.5 rounded-full border border-green-300 flex items-center gap-1">
                                    <span className="material-icons-round !text-xs">today</span>
                                    Today's Roll
                                </span>
                            )}
                            {existingCountForDate > 0 && (
                                <span className="bg-blue-100 text-blue-800 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-blue-200">
                                    Recorded ({existingCountForDate} students)
                                </span>
                            )}
                        </div>
                        <h2 className="text-xl font-bold text-indigo-950 mt-1">
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString(user.language || 'en', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </h2>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-gray-500">Select Date:</label>
                        <input
                            type="date"
                            value={selectedDate}
                            min={minDateStr}
                            max={maxDateStr}
                            onChange={(e) => {
                                if (e.target.value >= minDateStr && e.target.value <= maxDateStr) {
                                    setSelectedDate(e.target.value);
                                }
                            }}
                            className="bg-white border border-indigo-300 font-bold text-gray-800 text-xs rounded-lg px-2.5 py-1.5 shadow-sm focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>

                {/* Quick 1-click day buttons (Today down to 7 days ago) */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
                    <span className="text-[10px] font-bold text-gray-400 uppercase mr-1 flex-shrink-0">Past 7 Days:</span>
                    {quickDateOptions.map(opt => {
                        const isSelected = opt.dateStr === selectedDate;
                        return (
                            <button
                                key={opt.dateStr}
                                type="button"
                                onClick={() => setSelectedDate(opt.dateStr)}
                                className={`px-2.5 py-1 rounded-lg font-medium text-xs whitespace-nowrap transition-colors flex-shrink-0 ${
                                    isSelected
                                        ? 'bg-indigo-600 text-white font-bold shadow-sm'
                                        : 'bg-white text-gray-700 hover:bg-indigo-100/60 border border-gray-200'
                                }`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {students.map(student => {
                    const preReported = preReportedAbsences?.find(p => p.studentId === student.id && p.date === selectedDate);
                    return (
                        <div key={student.id} className="bg-gray-50 p-3 rounded-lg flex items-center justify-between border border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="flex items-center gap-3">
                                <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                <div>
                                    <p className="font-medium text-gray-800">{student.childName}</p>
                                    {preReported && <p className="text-xs text-orange-600 font-medium">Absent (Parent Reported: {preReported.reason})</p>}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <AttendanceButton status="present" isSelected={attendance[student.id] === 'present'} onClick={() => setStudentStatus(student.id, 'present')} />
                                <AttendanceButton status="absent" isSelected={attendance[student.id] === 'absent'} onClick={() => setStudentStatus(student.id, 'absent')} />
                                <AttendanceButton status="late" isSelected={attendance[student.id] === 'late'} onClick={() => setStudentStatus(student.id, 'late')} />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="p-4 bg-white border-t border-gray-200">
                <button
                    onClick={handleSave}
                    className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl"
                >
                    {translate('submit_attendance')}
                </button>
            </div>
        </div>
    );
};

// --- Student Attendance Detail Modal (Passbook) ---
export const StudentAttendanceDetailModal: FC<{
    student: StudentConnection;
    academicYear: AcademicYear;
    calendarEvents: CalendarEvent[];
    attendanceRecords: AttendanceRecord[];
    asOfDate?: string;
    onClose: () => void;
    t: (k: string) => string;
}> = ({ student, academicYear, calendarEvents, attendanceRecords, asOfDate, onClose, t }) => {
    const calculationDate = asOfDate || new Date().toISOString().split('T')[0];

    const instructionalDates = useMemo(() => {
        if (!academicYear?.startDate) return [];
        return getInstructionalDatesToDate(
            academicYear.startDate,
            academicYear.endDate,
            academicYear.offSaturdays || [2, 4],
            calendarEvents,
            student.classId,
            calculationDate
        );
    }, [academicYear, calendarEvents, student.classId, calculationDate]);

    const studentRecords = useMemo(() => {
        return attendanceRecords.filter(r => r.studentId === student.id && r.date <= calculationDate);
    }, [attendanceRecords, student.id, calculationDate]);

    const metrics = useMemo(() => {
        return computeStudentAttendanceMetrics(student.id, studentRecords, instructionalDates);
    }, [student.id, studentRecords, instructionalDates]);

    const monthlyLedger = useMemo(() => {
        return computeMonthlyAttendanceLedger(student.id, studentRecords, instructionalDates);
    }, [student.id, studentRecords, instructionalDates]);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-start pb-3 border-b">
                    <div className="flex items-center gap-3">
                        <Avatar name={student.childName} photoUrl={student.photoUrl} size="md" />
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">{student.childName}</h3>
                            <p className="text-xs text-gray-500">
                                Attendance Register &bull; As of {new Date(calculationDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>

                <div className="my-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <div className="p-3 bg-indigo-50/70 rounded-xl border border-indigo-100">
                        <div className={`text-2xl font-black ${
                            metrics.percentage >= 75 ? 'text-indigo-700' : metrics.percentage >= 65 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                            {metrics.percentage.toFixed(1)}%
                        </div>
                        <div className="text-[10px] text-indigo-900 font-bold uppercase mt-0.5">Running %</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="text-2xl font-bold text-gray-800">{metrics.effectiveTotalDays}</div>
                        <div className="text-[10px] text-gray-500 font-medium uppercase mt-0.5">Working Days</div>
                    </div>
                    <div className="p-3 bg-green-50/60 rounded-xl border border-green-100">
                        <div className="text-2xl font-bold text-green-700">{metrics.present}</div>
                        <div className="text-[10px] text-green-700 font-medium uppercase mt-0.5">Present (P)</div>
                    </div>
                    <div className="p-3 bg-red-50/60 rounded-xl border border-red-100">
                        <div className="text-2xl font-bold text-red-600">{metrics.absent}</div>
                        <div className="text-[10px] text-red-600 font-medium uppercase mt-0.5">Absent (A)</div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto border rounded-xl overflow-hidden shadow-2xs">
                    <table className="w-full text-left border-collapse text-xs">
                        <thead>
                            <tr className="bg-gray-100 text-gray-700 font-semibold">
                                <th className="p-2.5">Month</th>
                                <th className="p-2.5 text-center">Working Days</th>
                                <th className="p-2.5 text-center">Present</th>
                                <th className="p-2.5 text-center">Late</th>
                                <th className="p-2.5 text-center">Absent</th>
                                <th className="p-2.5 text-center">Month %</th>
                                <th className="p-2.5 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {monthlyLedger.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-4 text-center text-gray-400">
                                        No instructional days recorded up to this date.
                                    </td>
                                </tr>
                            ) : (
                                monthlyLedger.map(row => (
                                    <tr key={row.monthKey} className="hover:bg-gray-50">
                                        <td className="p-2.5 font-medium text-gray-800">{row.monthLabel}</td>
                                        <td className="p-2.5 text-center text-gray-600">{row.workingDays}</td>
                                        <td className="p-2.5 text-center font-bold text-green-600">{row.present}</td>
                                        <td className="p-2.5 text-center font-bold text-yellow-600">{row.late}</td>
                                        <td className="p-2.5 text-center font-bold text-red-500">{row.absent}</td>
                                        <td className="p-2.5 text-center">
                                            <span className={`inline-block px-1.5 py-0.5 rounded font-bold text-[10px] ${
                                                row.percentage >= 75 ? 'bg-green-100 text-green-800' : row.percentage >= 65 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {row.percentage.toFixed(0)}%
                                            </span>
                                        </td>
                                        <td className="p-2.5 text-center text-[10px] font-semibold text-gray-600">
                                            {row.percentage >= 75 ? 'Good' : row.percentage >= 65 ? 'Borderline' : 'Shortage'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bg-indigo-50/90 font-bold text-indigo-950 border-t-2 border-indigo-200">
                                <td className="p-2.5">Total to Date</td>
                                <td className="p-2.5 text-center font-extrabold">{metrics.effectiveTotalDays}</td>
                                <td className="p-2.5 text-center font-extrabold text-green-700">{metrics.present}</td>
                                <td className="p-2.5 text-center font-extrabold text-yellow-700">{metrics.late}</td>
                                <td className="p-2.5 text-center font-extrabold text-red-600">{metrics.absent}</td>
                                <td className="p-2.5 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-black ${
                                        metrics.percentage >= 75 ? 'bg-green-600 text-white' : metrics.percentage >= 65 ? 'bg-amber-500 text-white' : 'bg-red-600 text-white'
                                    }`}>
                                        {metrics.percentage.toFixed(1)}%
                                    </span>
                                </td>
                                <td className="p-2.5 text-center text-[11px]">
                                    {metrics.statusThreshold === 'healthy' ? 'Eligible' : metrics.statusThreshold === 'warning' ? 'Review' : 'Deficit'}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="pt-3 mt-3 border-t flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Class Attendance Register View (For Teachers & Principals) ---
export const ClassAttendanceRegisterView: FC<{
    cls: ClassRoom;
    academicYear: AcademicYear;
    students: StudentConnection[];
    attendanceRecords: AttendanceRecord[];
    calendarEvents: CalendarEvent[];
    onTakeAttendance?: () => void;
    t: (k: string) => string;
    isPrincipalView?: boolean;
}> = ({
    cls,
    academicYear,
    students,
    attendanceRecords,
    calendarEvents,
    onTakeAttendance,
    t,
    isPrincipalView = false
}) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const defaultDate = academicYear?.endDate && academicYear.endDate < todayStr ? academicYear.endDate : todayStr;

    const [asOfDate, setAsOfDate] = useState<string>(defaultDate);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'at_risk' | 'healthy'>('all');
    const [selectedStudentForModal, setSelectedStudentForModal] = useState<StudentConnection | null>(null);

    // Calculate official student instructional dates up to asOfDate
    const instructionalDates = useMemo(() => {
        if (!academicYear?.startDate) return [];
        return getInstructionalDatesToDate(
            academicYear.startDate,
            academicYear.endDate,
            academicYear.offSaturdays || [2, 4],
            calendarEvents,
            cls.id,
            asOfDate
        );
    }, [academicYear, calendarEvents, cls.id, asOfDate]);

    // Filter attendance records up to asOfDate
    const relevantRecords = useMemo(() => {
        return attendanceRecords.filter(r => r.classId === cls.id && r.date <= asOfDate);
    }, [attendanceRecords, cls.id, asOfDate]);

    const approvedStudents = useMemo(() => {
        return students.filter(s => s.classId === cls.id && s.approvalStatus === 'approved');
    }, [students, cls.id]);

    // Compute metrics for all approved students
    const studentMetricsList = useMemo(() => {
        return approvedStudents.map(student => {
            const studentRecs = relevantRecords.filter(r => r.studentId === student.id);
            const metrics = computeStudentAttendanceMetrics(student.id, studentRecs, instructionalDates);
            return {
                student,
                metrics
            };
        });
    }, [approvedStudents, relevantRecords, instructionalDates]);

    // Summary statistics for class
    const classSummary = useMemo(() => {
        const totalStudents = studentMetricsList.length;
        if (totalStudents === 0) {
            return {
                totalDaysHeld: instructionalDates.length,
                averagePercentage: 0,
                atRiskCount: 0,
                totalPresentMarks: 0,
                totalLateMarks: 0,
                totalAbsentMarks: 0
            };
        }

        const sumPercentage = studentMetricsList.reduce((acc, curr) => acc + curr.metrics.percentage, 0);
        const atRiskCount = studentMetricsList.filter(s => s.metrics.statusThreshold !== 'healthy').length;
        const totalPresentMarks = studentMetricsList.reduce((acc, curr) => acc + curr.metrics.present, 0);
        const totalLateMarks = studentMetricsList.reduce((acc, curr) => acc + curr.metrics.late, 0);
        const totalAbsentMarks = studentMetricsList.reduce((acc, curr) => acc + curr.metrics.absent, 0);

        return {
            totalDaysHeld: instructionalDates.length,
            averagePercentage: sumPercentage / totalStudents,
            atRiskCount,
            totalPresentMarks,
            totalLateMarks,
            totalAbsentMarks
        };
    }, [studentMetricsList, instructionalDates.length]);

    // Filter students by search and filter status
    const filteredList = useMemo(() => {
        return studentMetricsList.filter(item => {
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                if (!item.student.childName.toLowerCase().includes(query)) return false;
            }
            if (statusFilter === 'at_risk') {
                return item.metrics.statusThreshold !== 'healthy';
            }
            if (statusFilter === 'healthy') {
                return item.metrics.statusThreshold === 'healthy';
            }
            return true;
        });
    }, [studentMetricsList, searchQuery, statusFilter]);

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-4 sm:p-5 space-y-4">
            {/* Top Bar: Title + Date Filter + Action */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <span className="material-icons-round text-indigo-600">how_to_reg</span>
                        Class Attendance Register &amp; Ledger
                    </h3>
                    <p className="text-xs text-gray-500">
                        View present days, absent days, and running % at any point in time for each student
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Date Point-in-Time Selector */}
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5">
                        <span className="material-icons-round text-gray-500 !text-sm">event</span>
                        <label className="text-[11px] font-bold text-gray-600 uppercase">As of:</label>
                        <input
                            type="date"
                            value={asOfDate}
                            max={todayStr}
                            onChange={e => setAsOfDate(e.target.value)}
                            className="text-xs font-semibold text-gray-800 bg-transparent focus:outline-hidden"
                        />
                    </div>

                    {/* Take / Backfill Attendance button for teachers */}
                    {onTakeAttendance && (
                        <button
                            type="button"
                            onClick={onTakeAttendance}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                            <span className="material-icons-round !text-base">edit_calendar</span>
                            Take / Backfill Attendance
                        </button>
                    )}
                </div>
            </div>

            {/* Summary KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="text-2xl font-bold text-gray-800">{classSummary.totalDaysHeld}</div>
                    <div className="text-[11px] text-gray-500 font-medium uppercase mt-0.5">Instructional Days Held</div>
                </div>
                <div className="p-3 bg-indigo-50/70 rounded-xl border border-indigo-100">
                    <div className="text-2xl font-black text-indigo-700">
                        {classSummary.averagePercentage.toFixed(1)}%
                    </div>
                    <div className="text-[11px] text-indigo-900 font-bold uppercase mt-0.5">Class Average %</div>
                </div>
                <div className="p-3 bg-amber-50/60 rounded-xl border border-amber-100">
                    <div className={`text-2xl font-bold ${classSummary.atRiskCount > 0 ? 'text-amber-700' : 'text-gray-700'}`}>
                        {classSummary.atRiskCount}
                    </div>
                    <div className="text-[11px] text-amber-900 font-medium uppercase mt-0.5">Below 75% At-Risk</div>
                </div>
                <div className="p-3 bg-green-50/60 rounded-xl border border-green-100">
                    <div className="text-2xl font-bold text-green-700">{approvedStudents.length}</div>
                    <div className="text-[11px] text-green-700 font-medium uppercase mt-0.5">Enrolled Students</div>
                </div>
            </div>

            {/* Controls: Search + Filter Pills */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 pt-1">
                <div className="relative w-full sm:w-64">
                    <span className="material-icons-round absolute left-3 top-2.5 text-gray-400 !text-sm">search</span>
                    <input
                        type="text"
                        placeholder="Search student..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 border rounded-xl text-xs text-gray-800 bg-gray-50/60 focus:bg-white focus:outline-hidden"
                    />
                </div>

                <div className="flex gap-1.5 w-full sm:w-auto">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('all')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            statusFilter === 'all'
                                ? 'bg-gray-800 text-white shadow-xs'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        All ({studentMetricsList.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('at_risk')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${
                            statusFilter === 'at_risk'
                                ? 'bg-amber-600 text-white shadow-xs'
                                : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
                        }`}
                    >
                        <span className="material-icons-round !text-xs">warning</span>
                        Below 75% ({classSummary.atRiskCount})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('healthy')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            statusFilter === 'healthy'
                                ? 'bg-green-600 text-white shadow-xs'
                                : 'bg-green-50 text-green-800 hover:bg-green-100 border border-green-200'
                        }`}
                    >
                        Good Standing ({studentMetricsList.length - classSummary.atRiskCount})
                    </button>
                </div>
            </div>

            {/* Attendance Roster Table */}
            <div className="overflow-x-auto border rounded-xl overflow-hidden shadow-2xs">
                <table className="w-full text-left border-collapse text-xs">
                    <thead>
                        <tr className="bg-gray-100/80 text-gray-600 font-semibold border-b">
                            <th className="p-3">Student</th>
                            <th className="p-3 text-center">Working Days</th>
                            <th className="p-3 text-center">Present (P)</th>
                            <th className="p-3 text-center">Late (L)</th>
                            <th className="p-3 text-center">Absent (A)</th>
                            <th className="p-3 text-center">Running %</th>
                            <th className="p-3 text-center">Threshold</th>
                            <th className="p-3 text-center">Passbook</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredList.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="p-8 text-center text-gray-400">
                                    No students match the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredList.map(({ student, metrics }) => (
                                <tr key={student.id} className="hover:bg-gray-50/70 transition-colors">
                                    <td className="p-3 font-semibold text-gray-900 flex items-center gap-2.5">
                                        <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                        <div className="min-w-0">
                                            <p className="truncate text-xs font-bold text-gray-800">{student.childName}</p>
                                        </div>
                                    </td>
                                    <td className="p-3 text-center font-medium text-gray-700">
                                        {metrics.effectiveTotalDays}
                                    </td>
                                    <td className="p-3 text-center font-bold text-green-600">
                                        {metrics.present}
                                    </td>
                                    <td className="p-3 text-center font-bold text-yellow-600">
                                        {metrics.late}
                                    </td>
                                    <td className="p-3 text-center font-bold text-red-500">
                                        {metrics.absent}
                                    </td>
                                    <td className="p-3 text-center">
                                        <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-[11px] ${
                                            metrics.percentage >= 75 ? 'bg-green-100 text-green-800' : metrics.percentage >= 65 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {metrics.percentage.toFixed(1)}%
                                        </span>
                                    </td>
                                    <td className="p-3 text-center">
                                        {metrics.statusThreshold === 'healthy' ? (
                                            <span className="text-[11px] font-bold text-green-700 flex items-center justify-center gap-0.5">
                                                <span className="material-icons-round !text-xs text-green-600">check_circle</span>
                                                Eligible
                                            </span>
                                        ) : metrics.statusThreshold === 'warning' ? (
                                            <span className="text-[11px] font-bold text-amber-700 flex items-center justify-center gap-0.5">
                                                <span className="material-icons-round !text-xs text-amber-600">warning</span>
                                                Warning
                                            </span>
                                        ) : (
                                            <span className="text-[11px] font-bold text-red-600 flex items-center justify-center gap-0.5">
                                                <span className="material-icons-round !text-xs text-red-600">error</span>
                                                Critical
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-center">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedStudentForModal(student)}
                                            className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 mx-auto transition-colors"
                                            title="View student monthly passbook"
                                        >
                                            <span className="material-icons-round !text-xs">receipt_long</span>
                                            Passbook
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {/* Class Roster Totals Row */}
                    <tfoot>
                        <tr className="bg-indigo-50/90 font-bold border-t-2 border-indigo-200 text-indigo-950">
                            <td className="p-3 uppercase tracking-wide">
                                Class Summary ({filteredList.length} Students)
                            </td>
                            <td className="p-3 text-center font-extrabold text-indigo-900">
                                {classSummary.totalDaysHeld} Days Held
                            </td>
                            <td className="p-3 text-center font-extrabold text-green-700">
                                {classSummary.totalPresentMarks}
                            </td>
                            <td className="p-3 text-center font-extrabold text-yellow-700">
                                {classSummary.totalLateMarks}
                            </td>
                            <td className="p-3 text-center font-extrabold text-red-600">
                                {classSummary.totalAbsentMarks}
                            </td>
                            <td className="p-3 text-center">
                                <span className={`inline-block px-2 py-1 rounded-md text-xs font-black shadow-xs ${
                                    classSummary.averagePercentage >= 75 ? 'bg-green-600 text-white' : classSummary.averagePercentage >= 65 ? 'bg-amber-500 text-white' : 'bg-red-600 text-white'
                                }`}>
                                    Avg {classSummary.averagePercentage.toFixed(1)}%
                                </span>
                            </td>
                            <td className="p-3 text-center" colSpan={2}>
                                <span className="text-[11px] text-gray-500 font-normal">
                                    Calculated as of {asOfDate}
                                </span>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Modal for student's monthly breakdown */}
            {selectedStudentForModal && (
                <StudentAttendanceDetailModal
                    student={selectedStudentForModal}
                    academicYear={academicYear}
                    calendarEvents={calendarEvents}
                    attendanceRecords={attendanceRecords}
                    asOfDate={asOfDate}
                    onClose={() => setSelectedStudentForModal(null)}
                    t={t}
                />
            )}
        </div>
    );
};
